import {
    Button,
    Card,
    CardActionArea,
    CardContent,
    Checkbox,
    Grid,
    IconButton,
    Paper,
    TextField,
    Typography
} from '@material-ui/core';
import {
    createStyles,
    WithStyles,
    withStyles,
    Theme
} from '@material-ui/core/styles';
import { CloseOutlined } from '@material-ui/icons';
import _ from 'lodash';
import * as React from 'react';
import AccountProvider from '../../providers/account.provider';
import {
    SimpleListItem
} from '../../shared/components/simple-list';
import { SimpleModal } from '../../shared/components/simple-modal';
import { Team } from '../../shared/domain';
import TeamProvider from '../../providers/team.provider';
import { UserFull } from '../../models/user';
import { SimpleListPagination } from '../../shared/components/simple-list-pagination';
import { AccountFull } from '../../models/account';
import { CCSpinner } from '../../shared/components/cc-spinner';

interface UserSelectionList {
    userId: string;
    userEmail: string;
    team: string;
    checked: boolean;
}

const styles = (theme: Theme) =>
    createStyles({
        root: {
            display: 'flex',
            flexWrap: 'wrap'
        },
        container: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column'
        },
        treePaper: {
            width: '100%',
            height: '100%',
            minHeight: '40em',
            maxHeight: '50em',
            overflowY: 'scroll'
        },
        treeContainer: {
            background: '#fff',
            padding: '1em',
            height: '100%',
            minHeight: '40em',
            maxHeight: '50em'
        },
        buttonModal: {
            margin: '1em'
        },
        list: {
            flex: '1 0',
            overflow: 'scroll auto',
            boxShadow: 'none',
            maxHeight: '27em'
        },
        resumePopup: {
            width: '70%',
            borderRadius: 5
        },
        resumePopupHeader: {
            height: '2em',
            backgroundColor: theme.palette.primary.main,
            borderRadius: '3px 3px 0 0',
            color: 'white',
            padding: '0.5em',
            fontSize: '1.5em'
        },
        popupContentContainer: {
            display: 'flex',
            flexDirection: 'row',
            padding: theme.spacing(1)
        },
        heading: {
            fontWeight: 700,
            marginBottom: '0.5em',
            marginTop: '1em'
        },
        ellipsis: {
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden'
        },
        listModal: {
            padding: '0 1em',
            position: 'relative'
        },
        addButton: {
            width: '100%',
            margin: '0'
        },
        finishButton: {
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: '2%',
            width: '95%',
            margin: 'auto'
        },
        closeBtn: {
            position: 'absolute',
            zIndex: 999999,
            right: 0,
            top: 0,
            color: 'white'
        },
        errorHeading: {
            fontWeight: 500,
            marginBottom: '0.5em',
            marginTop: '0em',
            color: theme.palette.error.main,
            fontSize: 'small',
            textAlign: 'center',
            whiteSpace: 'break-spaces'
        },
        searchInputHolder: {
            display: 'flex',
            alignItems: 'center',
            marginBottom: '0.5em'
        },
        searchCategoryInput: {
            width: '40%'
        },
        searchInput: {
            width: '100%',
            margin: '0.5em'
        },
        emptyMarkerHolder: {
            height: '35em',
            display: 'flex',
            alignItems: 'center',
            '& h1': {
                width: '100%',
                textAlign: 'center',
                color: 'gray'
            }
        },

        markerCard: {
            position: 'relative',
            borderRadius: 0
        },
        markerCardBody: {
            padding: '0.5em 1em !important',
            '& h6': {
                fontSize: '1.3em',
                marginBottom: '0.5em',
                maxWidth: '80%'
            }
        },
        markerCheckbox: {
            position: 'absolute',
            right: '1em'
        },
        transferButton: {
            padding: '0.5em 0',
            margin: '1em 0'
        },
        footNote: {
            float: 'right',
            marginTop: '0.5em',
            color: 'gray',
            fontWeight: 'bold'
        },
        progressContainer: {
            flexGrow: 1
        },
        mainSpinnerLabel: {
            paddingBottom: '30px'
        },
    });

interface Props extends WithStyles<typeof styles> {
    currentAccount: AccountFull;
    accountTeams: Team[];
    existingList: string[];
    userKey: string;
    teamProvider: TeamProvider;
    isTeamsLoading: boolean;
    addButtonDataTestId: string;
    onUserChange?: (userKey: string, userIdsList: string[]) => void;
    onAuthError?: () => void;
}

interface State {
    isModalOpen: boolean;
    currentTeam: Team | null;
    areUsersLoading: boolean;
    teamList: SimpleListItem[];
    teamUsers: UserFull[];
    treeScrollPosition: number;
    isUserLoadWarning: boolean;
    userLoadWarning: string;
    allUserList: UserSelectionList[];
    searchUserList: UserSelectionList[];
    operationUserList: UserSelectionList[];
    allOperationUserList: UserSelectionList[];
    allUserSearchValue: string;
    operationUserSearchValue: string;
}

class TeamUserPicker extends React.Component<Props, State> {
    state: State = {
        isModalOpen: false,
        currentTeam: null,
        areUsersLoading: false,
        teamList: [],
        teamUsers: [],
        treeScrollPosition: 0,
        isUserLoadWarning: false,
        userLoadWarning: '',
        allUserList: [],
        searchUserList: [],
        operationUserList: [],
        allOperationUserList: [],
        allUserSearchValue: '',
        operationUserSearchValue: ''
    };

    accountProvider = new AccountProvider();

    componentDidMount() {
        this.formatTeamList();
    }

    formatTeamList = () => {
        const { accountTeams } = this.props;
        const teamList = accountTeams.map(e => {
            return new SimpleListItem(e.team_id, e.name, e.team_id);
        });
        this.setState({ teamList });
    };

    onModalToggle = () => {
        const { isModalOpen } = this.state;

        this.setState({
            isModalOpen: !isModalOpen,
            currentTeam: null
        });
    };

    onLoadTeamUsersClick = (listItem: SimpleListItem) => {
        const { accountTeams, teamProvider } = this.props;

        const pickedTeam = accountTeams.find(e => e.team_id === listItem.id);

        if (!pickedTeam) {
            return;
        }

        this.setState({ areUsersLoading: true });
        teamProvider
            .fetchTeamUsers(pickedTeam.team_id)
            .then(data => {
                const { users } = data;

                const userList: UserSelectionList[] = [];

                users.forEach(e => {
                    userList.push({
                        checked: false,
                        team: pickedTeam.name,
                        userEmail: e.email,
                        userId: e._id
                    });
                });

                this.setState({
                    allUserList: userList,
                    searchUserList: userList
                })
            })
            .catch(error => {
                console.error(error);
                const { status } = error.response;
                if (status === 401) {
                    const { onAuthError } = this.props;
                    if (!onAuthError) {
                        return;
                    }
                    onAuthError();
                    return;
                }
                this.setState({
                    isUserLoadWarning: true,
                    userLoadWarning: `Error loading users for team ${pickedTeam.name}`,
                    teamUsers: []
                });
            })
            .finally(() => {
                this.setState({
                    areUsersLoading: false
                });
            });
    };

    onFinishUserAdd = () => {
        const { onUserChange, userKey } = this.props;

        if (!onUserChange) {
            return;
        }

        const { operationUserList, teamList } = this.state;

        const userIdList = operationUserList.map(e => e.userId);
        this.setState({ operationUserList: [], teamList, isModalOpen: false }, () => {
            onUserChange(userKey, userIdList);
        });
    };

    centerScroll = (evt: React.UIEvent<HTMLElement>) => {
        this.setState({
            treeScrollPosition: evt.currentTarget.scrollTop
        });
    };

    onAllUsersSearch = (event: React.ChangeEvent<HTMLInputElement> & React.KeyboardEvent<HTMLInputElement>) => {
        const searchTxt = event.target.value.toLowerCase();

        const { allUserList } = this.state;

        if (searchTxt === '' || searchTxt.trim() === '') {
            this.setState({searchUserList: allUserList});
            return;
        }

        this.setState({
            searchUserList: allUserList.filter(e => e.userEmail.toLowerCase().indexOf(searchTxt) > -1),
            allUserSearchValue: searchTxt
        });
    };

    onOperationUsersSearch = (event: React.ChangeEvent<HTMLInputElement> & React.KeyboardEvent<HTMLInputElement>) => {
        const searchTxt = event.target.value.toLowerCase();

        const { allOperationUserList } = this.state;

        if (searchTxt === '' || searchTxt.trim() === '') {
            this.setState({searchUserList: allOperationUserList});
            return;
        }

        this.setState({
            operationUserList: allOperationUserList.filter(e => e.userEmail.toLowerCase().indexOf(searchTxt) > -1),
            operationUserSearchValue: searchTxt
        });
    };

    checkUser = (list: number, user: UserSelectionList, event?: React.ChangeEvent<HTMLInputElement>) => {
        if (list === 0) {
            const { allUserList } = this.state;
            const userIndex = allUserList.findIndex(m => m.userId === user.userId);
            allUserList[userIndex].checked = event ? event.target.checked : !allUserList[userIndex].checked;
            this.setState({allUserList});
        } else {
            const { operationUserList } = this.state;
            const userIndex = operationUserList.findIndex(m => m.userId === user.userId);
            operationUserList[userIndex].checked = event ? event.target.checked : !operationUserList[userIndex].checked;
            this.setState({operationUserList});
        }
    };

    handleAllSearch = () => {
        let { searchUserList, operationUserList } = this.state;
        operationUserList = operationUserList.concat(searchUserList);
        this.setState({
            searchUserList: [],
            operationUserList,
            allOperationUserList: operationUserList
        });
    };

    handleCheckedRight = () => {
        let { searchUserList, operationUserList } = this.state;

        const selected = searchUserList.filter(u => u.checked);
        operationUserList = operationUserList.concat(selected);
        searchUserList = searchUserList.filter(u => !u.checked);
        this.setState({
            operationUserList,
            searchUserList,
            allOperationUserList: operationUserList
        });
    };

    handleCheckedLeft = () => {
        let { searchUserList, operationUserList } = this.state;
        const selected = operationUserList.filter(m => m.checked);
        searchUserList = searchUserList.concat(selected);
        operationUserList = operationUserList.filter(m => !m.checked);
        this.setState({
            operationUserList,
            searchUserList,
            allOperationUserList: operationUserList
        });
    };

    handleAllOperations = () => {
        let { searchUserList, operationUserList } = this.state;
        searchUserList = searchUserList.concat(operationUserList);
        operationUserList = []
        this.setState({
            operationUserList,
            searchUserList,
            allOperationUserList: operationUserList
        });
    };

    public render() {
        const {
            classes,
            isTeamsLoading,
            addButtonDataTestId
        } = this.props;
        const {
            isModalOpen,
            teamList,
            allUserSearchValue,
            searchUserList,
            operationUserList,
            allOperationUserList,
            operationUserSearchValue,
            areUsersLoading
        } = this.state;

        return (
            <div className={`${classes.root}`} data-testid="team-user-picker">
                <div className={`${classes.container}`}>
                    {!isTeamsLoading && (
                        <Button
                            variant="contained"
                            color="primary"
                            className={classes.buttonModal}
                            onClick={this.onModalToggle}
                            data-testid={addButtonDataTestId}
                        >
                            Add Users
                        </Button>
                    )}
                    <SimpleModal
                        className={classes.resumePopup}
                        open={isModalOpen}
                        contentClasses={classes.popupContentContainer}
                        buttonOkLabel=""
                        buttonCancelLabel=""
                        header="User selection"
                        headerClassName={classes.resumePopupHeader}
                    >
                        <IconButton
                            className={classes.closeBtn}
                            onClick={this.onModalToggle}
                        >
                            <CloseOutlined />
                        </IconButton>
                        <Grid
                            container
                            spacing={1}
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Grid item xs={3}>
                                <Typography
                                    variant='h6'
                                    className={`${classes.heading} ${classes.ellipsis}`}
                                    >
                                    Account teams
                                </Typography>
                                <Paper className={classes.treePaper}>
                                    <SimpleListPagination
                                        className={classes.list}
                                        items={teamList}
                                        noItemsLabel={'No teams found'}
                                        canDeleteListItem={false}
                                        keepItemSelected={false}
                                        onListItemClick={
                                            this.onLoadTeamUsersClick
                                        }
                                        isLoading={isTeamsLoading}
                                        rowsPerPage={10}
                                        totalItems={teamList.length}
                                    />
                                </Paper>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography
                                    variant='h6'
                                    className={`${classes.heading} ${classes.ellipsis}`}
                                    >
                                    Users to select
                                </Typography>
                                <Paper className={classes.treePaper}>
                                    <div className={classes.searchInputHolder}>
                                        <TextField
                                            className={classes.searchInput}
                                            placeholder="Search users"
                                            value={allUserSearchValue}
                                            onChange={this.onAllUsersSearch}
                                            data-testid="all-search-input"
                                            variant="outlined"
                                            label="Search user by email"
                                        />
                                    </div>
                                    <CCSpinner
                                        label="Loading users"
                                        labelClassName={classes.mainSpinnerLabel}
                                        className={classes.progressContainer}
                                        loading={areUsersLoading}
                                        size={200}
                                    >
                                        {
                                            searchUserList.length <= 0 ?
                                            <div className={classes.emptyMarkerHolder}>
                                                <Typography variant="h6" component="h1">
                                                    No users to select
                                                </Typography>
                                            </div>
                                            :
                                            searchUserList.map(u => {
                                                return (
                                                    <Card className={classes.markerCard}>
                                                        <CardActionArea onClick={(e) => this.checkUser(0, u)}>
                                                            <CardContent className={classes.markerCardBody}>
                                                                <Checkbox
                                                                    className={classes.markerCheckbox}
                                                                    checked={u.checked}
                                                                    onChange={(e) => this.checkUser(0, u, e)}
                                                                />
                                                                <Typography variant="h6" component="h6" className={classes.ellipsis}>
                                                                    {u.userEmail}
                                                                </Typography>
                                                                <Typography color="textSecondary">
                                                                    {u.userId}
                                                                </Typography>
                                                            </CardContent>
                                                        </CardActionArea>
                                                    </Card>
                                                );
                                            })
                                        }
                                    </CCSpinner>
                                </Paper>
                                <div className={classes.footNote}>
                                    {searchUserList.length} Total Users
                                </div>
                            </Grid>
                            <Grid item xs={1}>
                                <Grid container direction="column" alignItems="center">
                                    <Button
                                        variant="contained"
                                        size="small"
                                        color="secondary"
                                        className={classes.transferButton}
                                        onClick={this.handleAllSearch}
                                        disabled={searchUserList.length === 0}
                                        aria-label="move all right"
                                        data-testid="move-all-right"
                                    >
                                        ≫
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        color="secondary"
                                        className={classes.transferButton}
                                        onClick={this.handleCheckedRight}
                                        disabled={searchUserList.filter(m => m.checked).length === 0}
                                        aria-label="move selected right"
                                        data-testid="move-selected-right"
                                    >
                                        &gt;
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        color="secondary"
                                        className={classes.transferButton}
                                        onClick={this.handleCheckedLeft}
                                        disabled={operationUserList.filter(m => m.checked).length === 0}
                                        aria-label="move selected left"
                                        data-testid="move-selected-left"
                                    >
                                        &lt;
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        color="secondary"
                                        className={classes.transferButton}
                                        onClick={this.handleAllOperations}
                                        disabled={operationUserList.length === 0}
                                        aria-label="move all left"
                                        data-testid="move-all-left"
                                    >
                                        ≪
                                    </Button>
                                </Grid>
                            </Grid>
                            <Grid item xs={4}>
                                <Typography
                                    variant='h6'
                                    className={`${classes.heading} ${classes.ellipsis}`}
                                    >
                                    Selected Users
                                </Typography>
                                <Paper className={classes.treePaper}>
                                    <div className={classes.searchInputHolder}>
                                        <TextField
                                            className={classes.searchInput}
                                            placeholder="Search users"
                                            value={operationUserSearchValue}
                                            onChange={this.onOperationUsersSearch}
                                            data-testid="operation-search-input"
                                            variant="outlined"
                                            label="Search user by email"
                                        />
                                    </div>
                                    {
                                        operationUserList.length <= 0 ?
                                        <div className={classes.emptyMarkerHolder}>
                                            <Typography variant="h6" component="h1">
                                                No users to select
                                            </Typography>
                                        </div>
                                        :
                                        operationUserList.map(u => {
                                            return (
                                            <Card className={classes.markerCard}>
                                                <CardActionArea onClick={(e) => this.checkUser(1, u)}>
                                                    <CardContent className={classes.markerCardBody}>
                                                        <Checkbox
                                                            className={classes.markerCheckbox}
                                                            checked={u.checked}
                                                            onChange={(e) => this.checkUser(1, u, e)}
                                                        />
                                                        <Typography variant="h6" component="h6">
                                                            {u.userEmail}
                                                        </Typography>
                                                        <Typography color="textSecondary">
                                                            {u.userId}
                                                        </Typography>
                                                    </CardContent>
                                                </CardActionArea>
                                            </Card>
                                            );
                                        })
                                    }
                                </Paper>
                                <div className={classes.footNote}>
                                    {operationUserList.length} Total Users
                                </div>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    className={classes.buttonModal}
                                    onClick={this.onFinishUserAdd}
                                    data-testid="add-team-user-picker"
                                    disabled={allOperationUserList.length <= 0}
                                >
                                    Add Users
                                </Button>
                            </Grid>
                        </Grid>
                    </SimpleModal>
                </div>
            </div>
        );
    }
}

const MUIComponent = withStyles(styles)(TeamUserPicker);
export { MUIComponent as TeamUserPicker };
